import { Aide } from '@/components/ui/aide';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  CheckCircle2,
  ClipboardCheck,
  Fuel,
  Gauge,
  MapPinned,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import { dashboardAPI } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { libelle, texteSur, titreSectionPilotage } from '../lib/libelles';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { cn, formatCurrency, formatVolume } from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { Readout } from '../components/ui/readout';
import { EmptyState } from '../components/ui/empty-state';
import { ONBOARDING_ROUTE } from '../components/onboarding';

const toneClass = {
  ok: 'border-success/30 bg-success/10 text-success',
  info: 'border-info/30 bg-info/10 text-info',
  warning: 'border-flame/30 bg-flame/10 text-flame-ink',
  risk: 'border-destructive/30 bg-destructive/10 text-destructive',
};

const TON_STAT = { ok: 'positif', warning: 'attention', risk: 'critique', info: 'neutre' };

const toneLabel = {
  ok: 'Maîtrisé',
  info: 'À suivre',
  warning: 'À traiter',
  risk: 'Risque',
};

const toneIcon = {
  ok: CheckCircle2,
  info: Gauge,
  warning: AlertTriangle,
  risk: AlertTriangle,
};

const modeMeta = {
  transport: {
    title: 'Vue d’ensemble · Transport et distribution',
    accent: 'text-flame-ink',
    icon: MapPinned,
  },
  stations: {
    title: 'Vue d’ensemble · Réseau de stations',
    accent: 'text-success',
    icon: Gauge,
  },
  control_fuel: {
    title: 'Vue d’ensemble · Contrôle du carburant',
    accent: 'text-info',
    icon: Fuel,
  },
  hybrid: {
    title: 'Vue d’ensemble · Groupe carburant',
    accent: 'text-process',
    icon: ShieldCheck,
  },
};

const formatValue = (metric) => {
  const value = Number(metric?.value || 0);
  if (metric?.format === 'money') return formatCurrency(value);
  if (metric?.format === 'liters') return formatVolume(value);
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
};

export default function PilotagePage() {
  const navigate = useNavigate();
  const [pilotage, setPilotage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setPilotage(await dashboardAPI.getBusinessCockpit());
    } catch (err) {
      setError(messageErreur(err, 'Impossible de charger la vue d’ensemble.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const meta = modeMeta[pilotage?.mode] || modeMeta.transport;
  const ModeIcon = meta.icon;
  const sections = useMemo(() => pilotage?.sections || [], [pilotage?.sections]);
  const priorityActions = pilotage?.priority_actions || [];
  const valuePromise = useMemo(() => valuePromiseByMode(pilotage?.mode), [pilotage?.mode]);
  const topMetrics = useMemo(() => collectTopMetrics(sections), [sections]);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="readouts" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-6">
          <p className="font-bold text-destructive">{error}</p>
          <Button className="mt-4" onClick={load}>
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Ce qui demande une décision"
        title={meta.title}
        description={valuePromise}
        stats={topMetrics.map((metric) => ({
          label: texteSur(metric.label, ''),
          valeur: formatValue(metric),
          ton: TON_STAT[metric.tone] || 'neutre',
        }))}
      />

      {pilotage?.headline && (
        <p className="text-lg font-semibold text-foreground">{texteSur(pilotage.headline, '')}</p>
      )}

      {/* Un cockpit sans donnée affichait un bandeau d'indicateurs vide
          au-dessus d'une colonne blanche, et la bande des missions annonçait
          « aucun blocage » — la pire phrase possible quand rien n'a encore
          été saisi. Tant qu'aucun domaine ne remonte, l'écran explique ce
          qu'il pilotera et mène au parcours de démarrage. */}
      {sections.length === 0 ? (
        <EmptyState
          icon={ModeIcon}
          title="Rien à afficher pour l’instant"
          description="Cette page rassemble ce qui demande une décision aujourd’hui : commandes en retard, camions immobilisés, factures impayées. Il se remplit dès que l’activité commence."
          actionLabel="Démarrer en 10 minutes"
          onAction={() => navigate(ONBOARDING_ROUTE)}
        />
      ) : (
      <>
      <section className="grid gap-4 xl:grid-cols-[1fr_420px]">
        <div className="space-y-4">
          <MissionStrip missions={priorityActions} onNavigate={navigate} />
          {sections.map((section) => (
            <CockpitSection key={section.key} section={section} onNavigate={navigate} />
          ))}
        </div>
        <aside className="space-y-4">
          <DecisionSnapshot sections={sections} />
          <BusinessRulesPanel />
        </aside>
      </section>
      </>
      )}
    </div>
  );
}

function MissionStrip({ missions, onNavigate }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-flame-ink">
            À faire en priorité
          </p>
          <h2>À traiter maintenant</h2>
        </div>
        <Badge className="w-fit border-border bg-muted/50 text-muted-foreground">
          {missions.length} décision{missions.length > 1 ? 's' : ''}
        </Badge>
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {missions.map((mission) => (
          <button
            key={`${mission.title}-${mission.route}`}
            type="button"
            onClick={() => onNavigate(mission.route)}
            className="rounded-lg border border-border bg-muted/50 p-4 text-left transition hover:border-flame/40 hover:bg-card hover:shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-black text-foreground">{texteSur(mission.title, '')}</p>
                <p className="mt-1 text-sm text-muted-foreground">{texteSur(mission.description, '')}</p>
              </div>
              <Badge className={toneClass[mission.tone] || toneClass.info}>
                {mission.count}
              </Badge>
            </div>
            <p className="mt-3 text-xs font-semibold text-muted-foreground">
              {texteSur(mission.action_contract?.expected_effect, '')}
            </p>
          </button>
        ))}
        {missions.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-muted/50 p-5 text-sm font-semibold text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              Aucun blocage majeur pour le moment.
              <Aide titre="Décisions urgentes">
                Les décisions urgentes — retards, impayés, immobilisations — s’afficheront ici.
              </Aide>
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function CockpitSection({ section, onNavigate }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">
            {section.key === 'group' ? 'Vue de tout le groupe' : 'Activité'}
          </p>
          <h2 className="mt-1">{titreSectionPilotage(section)}</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{texteSur(section.promise, '')}</p>
        </div>
        <ShieldCheck className="h-6 w-6 text-success" />
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-3 xl:grid-cols-5">
        {(section.metrics || []).map((metric) => (
          <MetricTile key={metric.label} metric={metric} compact />
        ))}
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {(section.missions || []).map((mission) => (
          <MissionCard key={mission.title} mission={mission} onNavigate={onNavigate} />
        ))}
      </div>
      {!!section.focus_queues?.length && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {section.focus_queues.map((queue) => (
            <FocusQueueCard key={queue.key} queue={queue} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </section>
  );
}

// Delegates to the shared Readout so a figure looks the same here as on every
// other screen. The tile used to tint its whole surface by tone; Readout puts
// the colour on the value, where it reads as a verdict rather than an alarm.
function MetricTile({ metric, compact = false }) {
  return (
    <Readout
      label={texteSur(metric.label, '')}
      value={formatValue(metric)}
      icon={toneIcon[metric.tone] || TrendingUp}
      tone={metric.tone}
      size={compact ? 'sm' : 'default'}
    />
  );
}

function MissionCard({ mission, onNavigate }) {
  const contract = mission.action_contract || {};
  return (
    <article className="rounded-lg border border-border bg-muted/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-black text-foreground">{texteSur(mission.title, '')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{texteSur(mission.description, '')}</p>
        </div>
        <Badge className={toneClass[mission.tone] || toneClass.info}>
          {toneLabel[mission.tone] || 'À suivre'} · {mission.count}
        </Badge>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <ContractBlock
          icon={ClipboardCheck}
          title="Pourquoi cette action"
          items={contract.preconditions || []}
        />
        <ContractBlock
          icon={Banknote}
          title="Ce qui change"
          items={[contract.expected_effect, ...(contract.linked_effects || [])].filter(Boolean)}
        />
      </div>
      <div className="mt-3 grid gap-2 rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground md:grid-cols-3">
        <p>
          <strong>Sans doublon :</strong> {operatorText(contract.idempotency)}
        </p>
        <p>
          <strong>Trace :</strong> {operatorText(contract.audit)}
        </p>
        <p>
          <strong>Si bloqué :</strong> {texteSur(contract.failure_message, 'Les conditions ne sont pas réunies.')}
        </p>
      </div>
      <Button className="mt-4 w-full justify-between" onClick={() => onNavigate(mission.route)}>
        Ouvrir
        <ArrowRight className="h-4 w-4" />
      </Button>
    </article>
  );
}

function ContractBlock({ icon: Icon, title, items }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-flame-ink" />
        <p className="text-xs font-black uppercase tracking-wide text-muted-foreground">{title}</p>
      </div>
      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{operatorText(item)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DecisionSnapshot({ sections }) {
  const missions = sections.flatMap((section) => section.missions || []);
  const risky = missions.filter((mission) => mission.tone === 'risk' && Number(mission.count || 0) > 0);
  const warning = missions.filter((mission) => mission.tone === 'warning' && Number(mission.count || 0) > 0);
  const moneyMetrics = sections
    .flatMap((section) => section.metrics || [])
    .filter((metric) => metric.format === 'money')
    .slice(0, 3);

  return (
    <Card className="border-border bg-card shadow-sm">
      <CardContent className="p-4">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">
          État d’exploitation
        </p>
        <h3 className="mt-1">Décisions visibles</h3>
        <div className="mt-4 space-y-3">
          <SnapshotLine icon={AlertTriangle} label="Blocages critiques" value={risky.length} tone="risk" />
          <SnapshotLine icon={ClockIcon} label="Actions à traiter" value={warning.length} tone="warning" />
          <SnapshotLine icon={Banknote} label="Indicateurs argent" value={moneyMetrics.length} tone="info" />
        </div>
        <div className="mt-4 space-y-2">
          {moneyMetrics.map((metric) => (
            <div key={metric.label} className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
              <span className="font-semibold text-muted-foreground">{texteSur(metric.label, '')}</span>
              <span className="font-black text-foreground">{formatValue(metric)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FocusQueueCard({ queue, onNavigate }) {
  return (
    <article className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-wide text-muted-foreground">À suivre</p>
          <p className="mt-1 font-black text-foreground">{texteSur(queue.title, '')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{texteSur(queue.description, '')}</p>
        </div>
        <Badge className="border-border bg-muted/50 text-muted-foreground">
          {queue.items?.length || 0}
        </Badge>
      </div>
      <div className="mt-4 space-y-2">
        {(queue.items || []).map((item) => (
          <button
            key={`${queue.key}-${item.trip_id || item.order_id || item.title}`}
            type="button"
            onClick={() => onNavigate(item.route || '/finance')}
            className="flex w-full items-start justify-between gap-3 rounded-lg border border-border bg-muted/50 px-3 py-3 text-left transition hover:border-flame/40 hover:bg-card"
          >
            <div>
              <p className="font-bold text-foreground">{texteSur(item.title, '')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{texteSur(item.subtitle, '')}</p>
            </div>
            <div className="text-right">
              <Badge className={toneClass[item.tone] || toneClass.info}>
                {toneLabel[item.tone] || 'À suivre'}
              </Badge>
              <p className="mt-2 text-sm font-black text-foreground">
                {formatCurrency(Number(item.value || 0))}
              </p>
              <p className="mt-1 text-xs font-semibold text-muted-foreground">
                {texteSur(item.action_label, 'Ouvrir')}
              </p>
            </div>
          </button>
        ))}
        {!(queue.items || []).length && (
          <div className="rounded-lg border border-dashed border-border bg-muted/50 p-4 text-sm font-semibold text-muted-foreground">
            {texteSur(queue.empty_state, 'Rien à signaler.')}
          </div>
        )}
      </div>
    </article>
  );
}

function BusinessRulesPanel() {
  const rules = [
    'Un mouvement de stock n’est compté qu’une seule fois par opération.',
    'Une facture n’est émise que si le volume et la livraison concordent.',
    'Une sortie de carburant est valorisée dès que le prix du litre est connu.',
    'Chaque blocage affiche sa raison.',
  ];
  return (
    <Card className="border-success/30 bg-success/10 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-success" />
          <h3 className="text-success">Règles de contrôle</h3>
          <Aide titre="Règles de contrôle" points={rules} />
        </div>
      </CardContent>
    </Card>
  );
}

function SnapshotLine({ icon: Icon, label, value, tone }) {
  return (
    <div className={cn('flex items-center justify-between rounded-lg border px-3 py-2', toneClass[tone])}>
      <span className="flex items-center gap-2 text-sm font-bold">
        <Icon className="h-4 w-4" />
        {label}
      </span>
      <span className="text-base font-bold tabular-nums">{value}</span>
    </div>
  );
}

function ClockIcon(props) {
  return <Gauge {...props} />;
}

function collectTopMetrics(sections) {
  const preferred = ['Prêt à facturer FCFA', 'Cash bloqué', 'Cash bloqué preuve', 'Créances ouvertes'];
  const metrics = sections.flatMap((section) =>
    (section.metrics || []).map((metric) => ({ ...metric, section: section.key })),
  ).filter(Boolean);
  return metrics
    .sort((a, b) => {
      const aPreferred = preferred.includes(texteSur(a.label, '')) ? 0 : 1;
      const bPreferred = preferred.includes(texteSur(b.label, '')) ? 0 : 1;
      if (aPreferred !== bPreferred) return aPreferred - bPreferred;
      if (a.tone === 'risk' && b.tone !== 'risk') return -1;
      if (b.tone === 'risk' && a.tone !== 'risk') return 1;
      return 0;
    })
    .slice(0, 4);
}

function operatorText(value) {
  return libelle('pilotageContrat', value, '');
}

function valuePromiseByMode(mode) {
  if (mode === 'stations') {
    return "Pour un réseau de stations: ventes, cuves, achats, caisse, marge et risques remontent avant qu'une rupture ou un écart caisse devienne invisible.";
  }
  if (mode === 'control_fuel') {
    return "Pour un gros consommateur: demandes, sorties, engins et coûts deviennent traçables, avec une lecture immédiate des validations et des dépenses.";
  }
  if (mode === 'hybrid') {
    return "Pour un groupe hybride: le litre est suivi depuis l'import jusqu'à la station ou l'engin, avec stock consolidé et anomalies croisées.";
  }
  return "Pour un importateur-distributeur: commandes, camions, douane, livraison, volume, stock et facture sont reliés dans une chaîne contrôlable.";
}
